import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BookOpenIcon, 
  UserIcon, 
  ChartBarIcon, 
  BoltIcon,
  ClockIcon,
  StarIcon,
  TrophyIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon,
  VideoCameraIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { 
  PlayIcon as PlayIconSolid
} from '@heroicons/react/24/solid';
import apiService from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import LiveSessionNotification from './LiveSessionNotification';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = ({ onLogout }) => {
  const { userProfile, avatarUrl } = useAuth();
  const profileData = userProfile?.profile || userProfile?.student_profile || {};
  const studentName = (
    userProfile?.full_name ||
    profileData.full_name ||
    [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') ||
    [profileData.first_name, profileData.last_name].filter(Boolean).join(' ') ||
    userProfile?.username ||
    'Learner'
  ).trim();
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const avatarMenuRef = useRef(null);
  const initials = studentName
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .slice(0, 2)
    .join('');

  useEffect(() => {
    if (!isAvatarMenuOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(event.target)) {
        setIsAvatarMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsAvatarMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isAvatarMenuOpen]);

  const handleLogoutClick = () => {
    setIsAvatarMenuOpen(false);
    onLogout();
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState({
    totalCourses: 0,
    completedCourses: 0,
    studyStreak: 0,
    totalHours: 0,
    badges: 0,
    rank: "Learner"
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoError, setLogoError] = useState(false);

  // Load courses and stats from API
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if user is authenticated
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('Please log in to access your dashboard');
          setLoading(false);
          return;
        }

        // Load student's enrolled courses
        const studentCourses = await apiService.getStudentCourses();
        
        // Transform course data to match frontend format
        const transformedCourses = studentCourses.map((course, index) => ({
          id: course.id,
          title: course.name,
          description: course.description,
          progress: course.progress || 0, // Use real progress data
          totalLessons: course.total_materials || 0, // Use real material count
          completedLessons: course.completed_materials || 0, // Use real completed count
          difficulty: course.difficulty || "Beginner",
          estimatedTime: `${course.duration_months} months`,
          image: ["🌐", "🐍", "☁️", "🤖", "📊", "🎨"][index % 6],
          color: [
            "from-blue-500 to-purple-600",
            "from-green-500 to-teal-600", 
            "from-orange-500 to-red-600",
            "from-purple-500 to-pink-600",
            "from-indigo-500 to-blue-600",
            "from-red-500 to-orange-600"
          ][index % 6],
          nextLesson: course.next_lesson || "Continue Learning",
          instructor: course.instructor_name || "Aitech Instructor",
          batchName: course.batch_name,
          enrollmentDate: course.enrollment_date,
          classType: course.class_type
        }));

        setCourses(transformedCourses);

        // Load real student stats
        const studentStats = await apiService.getStudentStats();
        setStats(studentStats);

      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        
        // Provide more specific error messages
        let errorMessage = 'Failed to load dashboard data. ';
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          errorMessage += 'Please make sure the backend server is running on http://127.0.0.1:8000';
        } else if (err.message.includes('404')) {
          errorMessage += 'Some API endpoints may not be available yet.';
        } else {
          errorMessage += 'Please try again or contact support.';
        }
        
        setError(errorMessage);
        
        // Set some fallback data so the dashboard isn't completely empty
        const fallbackCourses = [
          {
            id: 'fallback-1',
            title: "Sample Course - Backend Offline",
            description: "This is sample data shown when the backend is not available",
            progress: 0,
            totalLessons: 10,
            completedLessons: 0,
            difficulty: "Beginner",
            estimatedTime: "8 weeks",
            image: "📚",
            color: "from-gray-500 to-gray-600",
            nextLesson: "Start Learning",
            instructor: "Aitech Team"
          }
        ];
        
        setCourses(fallbackCourses);
        
        const fallbackStats = {
          totalCourses: 0,
          completedCourses: 0,
          studyStreak: 0,
          totalHours: 0,
          badges: 0,
          rank: "Learner"
        };
        
        setStats(fallbackStats);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Helper function to parse duration and convert to comparable number (in weeks)
  const parseDurationToWeeks = (duration) => {
    if (!duration) return 0;
    
    const durationStr = duration.toLowerCase();
    const numberMatch = durationStr.match(/(\d+(?:\.\d+)?)/);
    
    if (!numberMatch) return 0;
    
    const number = parseFloat(numberMatch[1]);
    
    if (durationStr.includes('month')) {
      return number * 4.33; // Convert months to weeks (average 4.33 weeks per month)
    } else if (durationStr.includes('week')) {
      return number;
    } else if (durationStr.includes('year')) {
      return number * 52; // Convert years to weeks
    }
    
    // Default to weeks if no clear unit
    return number;
  };

  // Helper function to filter courses based on search query
  const filterCourses = (courses, query) => {
    if (!query.trim()) return courses;
    
    const searchTerm = query.toLowerCase().trim();
    
    return courses.filter(course => {
      const searchableFields = [
        course.title,
        course.description,
        course.instructor,
        course.difficulty,
        course.nextLesson
      ].map(field => (field || '').toLowerCase());
      
      return searchableFields.some(field => field.includes(searchTerm));
    });
  };

  // Apply search filter to courses and sort them
  const filteredCourses = filterCourses(courses, searchQuery).sort((a, b) => {
    // Sort by duration in descending order (longest first)
    const durationA = parseDurationToWeeks(a.estimatedTime);
    const durationB = parseDurationToWeeks(b.estimatedTime);
    return durationB - durationA;
  });

  const CourseCard = ({ course }) => {
    const navigate = useNavigate();
    
    const handleCardClick = () => {
      navigate(`/learn/${course.id}`);
    };

    return (
      <div 
        className="card group cursor-pointer hover:shadow-lg transition-shadow duration-300" 
        onClick={handleCardClick}
      >
        <div className="flex items-center justify-between mb-4">
          <div className={`w-12 h-12 bg-gradient-to-r ${course.color} rounded-xl flex items-center justify-center text-2xl`}>
            {course.image}
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            course.difficulty === 'Beginner' ? 'bg-green-100 text-green-800' :
            course.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800'
          }`}>
            {course.difficulty}
          </span>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
          {course.title}
        </h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {course.description}
        </p>

        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Progress</span>
            <span className="font-semibold text-primary-600">{course.progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`bg-gradient-to-r ${course.color} h-2 rounded-full transition-all duration-300`}
              style={{ width: `${course.progress}%` }}
            ></div>
          </div>
          
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center">
              <BookOpenIcon className="w-4 h-4 mr-1" />
              {course.completedLessons}/{course.totalLessons} lessons
            </div>
            <div className="flex items-center">
              <ClockIcon className="w-4 h-4 mr-1" />
              {course.estimatedTime}
            </div>
          </div>

          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1">Next Lesson</p>
                <p className="text-sm font-medium text-gray-900">{course.nextLesson}</p>
              </div>
              <div className="btn-primary text-sm py-2 px-4 flex items-center">
                <PlayIconSolid className="w-4 h-4 mr-1" />
                Continue
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const StatsCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <div className="card text-center">
      <div className={`w-12 h-12 bg-gradient-to-r ${color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
      <p className="text-sm font-medium text-gray-700 mb-1">{title}</p>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </div>
  );

  const LiveSessionsPreview = () => {
    const [liveSessions, setLiveSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchLiveSessions = async () => {
        try {
          const response = await apiService.getMyLiveSessions();
          setLiveSessions(Array.isArray(response) ? response : []);
        } catch (error) {
          console.error('Failed to fetch live sessions:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchLiveSessions();
    }, []);

    const upcomingSessions = liveSessions.filter(session => 
      session.status === 'scheduled' || session.status === 'draft'
    ).slice(0, 3);

    const liveNowSessions = liveSessions.filter(session => 
      session.status === 'live'
    ).slice(0, 3);

    if (loading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="card bg-white p-6">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded mb-4"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
          <div className="card bg-white p-6">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded mb-4"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Upcoming Sessions */}
        <div className="card bg-white p-6">
          <h4 className="text-lg font-bold text-gray-900 mb-4">Upcoming Sessions</h4>
          {upcomingSessions.length > 0 ? (
            <div className="space-y-3">
              {upcomingSessions.map((session) => (
                <div key={session.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{session.title}</p>
                    <p className="text-xs text-gray-600">{session.course_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-blue-600 font-medium">
                      {new Date(session.scheduled_time).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(session.scheduled_time).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No upcoming sessions</p>
          )}
        </div>

        {/* Live Sessions */}
        <div className="card bg-white p-6">
          <h4 className="text-lg font-bold text-gray-900 mb-4">Live Now</h4>
          {liveNowSessions.length > 0 ? (
            <div className="space-y-3">
              {liveNowSessions.map((session) => (
                <div key={session.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{session.title}</p>
                    <p className="text-xs text-gray-600">{session.course_name}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center text-green-600">
                      <div className="w-2 h-2 bg-green-600 rounded-full mr-1 animate-pulse"></div>
                      <span className="text-xs font-medium">LIVE</span>
                    </div>
                    <Link 
                      to={`/live-sessions`}
                      className="text-xs text-green-600 hover:text-green-700 font-medium"
                    >
                      Join Now
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No live sessions</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="glass-effect border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              {!logoError ? (
                <img 
                  src="/images/aitech-dark.png" 
                  alt="Aitech Logo" 
                  className="h-10 w-auto mr-3"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <div>
                  <h1 className="text-xl font-bold text-gray-900">
                    <span className="text-primary-600">AI</span>TECH
                  </h1>
                  <p className="text-xs text-gray-500">Technologies Nigeria</p>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-4">
              <button className="relative p-2 text-gray-600 hover:text-primary-600 transition-colors">
                <ChatBubbleLeftRightIcon className="w-6 h-6" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
              </button>
              
              <LiveSessionNotification />
              
              <Link 
                to="/live-sessions"
                className="relative p-2 text-gray-600 hover:text-primary-600 transition-colors"
                title="Live Sessions"
              >
                <VideoCameraIcon className="w-6 h-6" />
              </Link>
              
              <div className="relative flex items-center space-x-3" ref={avatarMenuRef}>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{studentName}</p>
                  <p className="text-xs text-gray-500">{stats.rank}</p>
                </div>
                <button
                  onClick={() => setIsAvatarMenuOpen((prev) => !prev)}
                  className="w-10 h-10 rounded-full border border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center overflow-hidden"
                  aria-haspopup="true"
                  aria-expanded={isAvatarMenuOpen}
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={`${studentName}'s avatar`}
                      className="w-full h-full object-cover"
                    />
                  ) : initials ? (
                    <span className="text-white font-semibold">{initials}</span>
                  ) : (
                    <UserIcon className="w-6 h-6 text-white" />
                  )}
                </button>

                {isAvatarMenuOpen && (
                  <div className="absolute right-0 top-12 w-48 rounded-xl border border-gray-100 bg-white shadow-xl py-2 z-50">
                    <div className="px-4 pb-2">
                      <p className="text-sm font-medium text-gray-900">{studentName}</p>
                      <p className="text-xs text-gray-500">{profileData.email || userProfile?.email}</p>
                    </div>
                    <div className="my-2 border-t border-gray-100"></div>
                    <Link
                      to="/settings"
                      onClick={() => setIsAvatarMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Cog6ToothIcon className="w-5 h-5 mr-3 text-gray-400" />
                      Settings
                    </Link>
                    <button
                      onClick={handleLogoutClick}
                      className="w-full flex items-center px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50"
                    >
                      <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, Student! 👋
          </h2>
          <p className="text-gray-600">
            Continue your learning journey and unlock your potential
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="large" message="Loading your dashboard..." />
          </div>
        )}

        {/* Error State - Show as banner but still display content */}
        {error && (
          <div className="card border-red-200 bg-red-50 mb-6">
            <div className="flex items-center">
              <div className="text-red-600 mr-3">⚠️</div>
              <div className="flex-grow">
                <h3 className="text-red-800 font-medium">Backend Connection Issue</h3>
                <p className="text-red-700 text-sm mt-1">{error}</p>
                <p className="text-red-600 text-xs mt-1">Showing sample data below. Start your backend server to see real data.</p>
              </div>
              <button 
                onClick={() => window.location.reload()} 
                className="btn-secondary text-sm ml-4"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Main Dashboard Content */}
        {!loading && (
          <>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <StatsCard 
            icon={BookOpenIcon}
            title="Active Courses"
            value={stats.totalCourses}
            subtitle="In progress"
            color="from-blue-500 to-blue-600"
          />
          <StatsCard 
            icon={TrophyIcon}
            title="Completed"
            value={stats.completedCourses}
            subtitle="Courses finished"  
            color="from-green-500 to-green-600"
          />
          <StatsCard 
            icon={BoltIcon}
            title="Study Streak"
            value={`${stats.studyStreak} days`}
            subtitle="Keep it up!"
            color="from-orange-500 to-orange-600"
          />
          <StatsCard 
            icon={StarIcon}
            title="Badges Earned"
            value={stats.badges}
            subtitle="Achievements"
            color="from-purple-500 to-purple-600"
          />
        </div>

        {/* Live Sessions Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Live Sessions</h3>
            <Link 
              to="/live-sessions"
              className="btn-primary text-sm flex items-center"
            >
              <VideoCameraIcon className="w-4 h-4 mr-2" />
              View All Sessions
            </Link>
          </div>
          
          <LiveSessionsPreview />
        </div>

        {/* Courses Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Your Courses</h3>
            <div className="flex items-center space-x-2">
              <button className="btn-secondary text-sm">
                <ChartBarIcon className="w-4 h-4 mr-2" />
                View Analytics
              </button>
            </div>
          </div>

          {/* Search Section */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search your courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 text-sm transition-all duration-200"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            
            <div className="text-sm text-gray-600">
              {searchQuery ? (
                <>
                  {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''} found
                  {filteredCourses.length === 0 && (
                    <span className="text-orange-600 ml-2">No courses match your search</span>
                  )}
                </>
              ) : (
                `${filteredCourses.length} active course${filteredCourses.length !== 1 ? 's' : ''}`
              )}
            </div>
          </div>

          <div className="card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Course</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Progress</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Lessons</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Difficulty</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Time Est.</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Next Lesson</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCourses.map((course) => (
                    <tr key={course.id} className="hover:bg-gray-50 transition-colors">
                      {/* Course Info */}
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className={`w-10 h-10 bg-gradient-to-r ${course.color} rounded-xl flex items-center justify-center text-lg mr-4 flex-shrink-0`}>
                            {course.image}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-bold text-gray-900 truncate max-w-xs">
                              {course.title}
                            </div>
                            <div className="text-xs text-gray-500 truncate max-w-xs">
                              by {course.instructor}
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      {/* Progress */}
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-500">Progress</span>
                            <span className="text-xs font-semibold text-primary-600">{course.progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`bg-gradient-to-r ${course.color} h-2 rounded-full transition-all duration-300`}
                              style={{ width: `${course.progress}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      
                      {/* Lessons */}
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {course.completedLessons}/{course.totalLessons}
                        </div>
                        <div className="text-xs text-gray-500">lessons</div>
                      </td>
                      
                      {/* Difficulty */}
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          course.difficulty === 'Beginner' ? 'bg-green-100 text-green-800' :
                          course.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {course.difficulty}
                        </span>
                      </td>
                      
                      {/* Time Estimate */}
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <ClockIcon className="w-4 h-4 mr-1" />
                          {course.estimatedTime}
                        </div>
                      </td>
                      
                      {/* Next Lesson */}
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                          {course.nextLesson}
                        </div>
                      </td>
                      
                      {/* Action */}
                      <td className="px-6 py-4">
                        <Link 
                          to={`/learn/${course.id}`}
                          className="inline-flex items-center px-3 py-1 bg-primary-600 text-white font-medium text-xs rounded-md hover:bg-primary-700 transition-colors"
                        >
                          <PlayIconSolid className="w-3 h-3 mr-1" />
                          Continue
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* AI Tutor Quick Access */}
        <div className="card bg-gradient-to-r from-primary-50 to-purple-50 border-primary-200">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">AI Tutor Assistant</h4>
              <p className="text-gray-600 text-sm mb-4">
                Get instant help with your coursework and personalized learning recommendations
              </p>
              <button className="btn-primary text-sm">
                <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                Ask AI Tutor
              </button>
            </div>
            <div className="text-6xl opacity-20">🤖</div>
          </div>
        </div>
        </>
        )}
      </main>
    </div>
  );
};

export default Dashboard; 